import { getIdToken } from '../lib/auth-service';

// ── Chat control API (Matrix Chat Host, Tier-1 platform service) ──
// Talks to the Peregrine chat control plane, NOT the ops API. The base URL is
// the chat control API Gateway; override via VITE_CHAT_API_URL.

const CHAT_API_URL =
  import.meta.env.VITE_CHAT_API_URL ||
  'https://hpnhisyuxi.execute-api.us-east-1.amazonaws.com/dev';

export type ChatTier = 'free' | 'paid';
export type ChatRoomKind = 'support' | 'bridged_teams' | 'bridged_slack';

export interface ChatRoom {
  room_id: string;
  tenant_id: string;
  kind: ChatRoomKind;
  tier: ChatTier;
  account_id: string | null;
  linked_ticket_id: string | null;
  created_at: string;
}

async function chatFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const token = await getIdToken();
  if (!token) throw new Error('Not authenticated');
  const response = await fetch(`${CHAT_API_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...options?.headers,
    },
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body?.error?.message || `Chat API error: ${response.status}`);
  }
  return response.json();
}

/** Cross-tenant inbox for ops (gdna_agent/gdna_admin). */
export async function fetchAllRooms(): Promise<{ scope: string; rooms: ChatRoom[] }> {
  return chatFetch('/chat/rooms');
}

/** Rooms for a specific tenant. */
export async function fetchTenantRooms(tenantId: string): Promise<{ tenant_id: string; rooms: ChatRoom[] }> {
  return chatFetch(`/chat/rooms?tenant_id=${encodeURIComponent(tenantId)}`);
}

/** Set a room's tier (gdna_admin only). */
export async function setRoomTier(
  roomId: string,
  tier: ChatTier,
  mspSlaRef?: string,
): Promise<{ room_id: string; tier: ChatTier; msp_sla_ref: string | null }> {
  return chatFetch(`/chat/rooms/${encodeURIComponent(roomId)}/tier`, {
    method: 'PUT',
    body: JSON.stringify({ tier, msp_sla_ref: mspSlaRef }),
  });
}

/** Spawn a CS support-ticket from a chat room. */
export async function spawnTicket(
  roomId: string,
  summary?: string,
): Promise<{ room_id: string; ticket_id: string; status: string }> {
  return chatFetch(`/chat/rooms/${encodeURIComponent(roomId)}/ticket`, {
    method: 'POST',
    body: JSON.stringify({ summary }),
  });
}
